import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PageLoader } from './ui_elements/LoadingSpinner';
import type { Role } from '../types';
import { isStudentRole } from '../permissions';
import PendingAccountScreen from './PendingAccountScreen';
import { loginPathFor } from '../utils/returnPath';

// Protected Route Component
//
// The default admits every real role, students and leaders included - the web
// app is open to the whole cast, and what each person sees inside it is
// decided per capability (src/permissions), not here. Anything that is not a
// known role (an `unassigned` profile, a half-provisioned account) falls
// through to Access Denied.
const ProtectedRoute: React.FC<{ children: React.ReactNode, requiredRole?: Role[] }> = ({
  children,
  requiredRole = ['student']
}) => {
  const { user, profile, loading, hasRole, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user || !profile) {
    // Carry the destination through sign-in, so a link from an email or a
    // bookmark still lands where it pointed (utils/returnPath).
    return <Navigate to={loginPathFor(location)} replace />;
  }

  if (!hasRole(requiredRole)) {
    // For other roles (unauthorized access)
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-fg mb-4">
            Access Denied
          </h1>
          <p className="text-fg-secondary mb-6">
            You don't have permission to access this page
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Go to Shows
          </button>
          {/* "/" is itself protected, so a profile with no usable role would
              land straight back here - signing out is the only way forward. */}
          <button
            onClick={() => { void logout(); }}
            className="block mx-auto mt-4 text-sm text-fg-secondary hover:text-fg underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // A student who is not activated cannot read show content (rules gate it
  // on accountStatus), so hold them on the finish-setting-up screen rather
  // than a dashboard of load errors. A missing status reads as activated,
  // matching the rules. Staff are never held - their rules do not check it.
  if (isStudentRole(profile.role) && profile.accountStatus && profile.accountStatus !== 'activated') {
    return (
      <PendingAccountScreen
        email={user.email || profile.email}
        disabled={profile.accountStatus === 'disabled'}
        onSignOut={() => { void logout(); }}
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
